import { useMemo, useState } from 'react'
import Rating from './Rating'
import './AuditComponent.scss'
import MultipleChoiceQuestion from './MultipleChoiceQuestion'
import CheckboxQuestion from './CheckboxQuestion'
import Accordion from './Accordion.jsx'
import ViewGradingFlow from './ViewGradingFlow'
import ToolTip from './Tooltip'
import { useGuidanceManager } from './GuidanceIcon'
import { CriteriaComment } from './CriteriaComment'

const gradingFlow = (module) => {
  if (module === 'QEPA') return <ViewGradingFlow />

  return ''
}

const QuestionType = ({
  question: { type, id, questionAnswers, children, ...rest },
  disabled,
  isValidating,
  answers,
  setAnswers,
  questions,
  multipleIndex,
  validate,
  conditions,
  guidanceManager
}) => {
  const map = {
    'multiple-choice': MultipleChoiceQuestion,
    'yes/no': CheckboxQuestion
  }

  const Question = map[type]

  const multipleChildren = useMemo(() => children?.filter((c) => !c.answerId), [children])
  const showChildren = useMemo(() => children?.filter((c) => c.answerId).sort((a, b) => a.index - b.index), [children])

  const value = multipleIndex ? answers.answers?.find((a) => a.questionId === id && multipleIndex === a.index) : answers.answers?.find((a) => a.questionId === id)
  const options = questionAnswers.map(({ id, options: { label, value }, ...rest }) => ({ ...rest, label: label, value: id, valueInput: value }))
  const selected = options.find(({ value: v }) => v === value?.answerId)

  /**
   * Returns a child question count, handling -1 for not applicables.
   * @param {undefined|null|Number} num - child count.
   * @returns {Number}
   */
  const getChildCount = (num) => {
    if (isNaN(parseInt(num)) || parseInt(num) < 0) return 0
    return parseInt(num)
  }

  return (
    <>
      <Question
        key={id}
        disabled={disabled}
        options={options}
        answer={selected ? { ...selected, comment: value?.comment } : selected}
        onChange={(newAnswer) => {
          const { value: answerId, valueInput, comment } = newAnswer
          setAnswers({
            comment: answers?.comment,
            answer: {
              answerId,
              questionResponseId: answers?.questionResponseId,
              questionId: id,
              index: multipleIndex,
              comment,
              valueInput
            }
          })
        }}
        guidanceManager={guidanceManager}
        {...rest}
      />
      {multipleChildren?.length > 0 &&
        [...Array(getChildCount(questionAnswers?.find((a) => a.id === value?.answerId)?.options.value))].map((v, n) => (
          <div className='multiple' key={n}>
            <div className='number-ball'>
              <p>{n + 1}</p>
            </div>
            {multipleChildren.map(({ childId }) => {
              const question = questions.find(({ id }) => id === childId)
              return (
                <QuestionType
                  key={`${question.id}-${multipleIndex}-${n + 1}`}
                  question={question}
                  isValidating={isValidating}
                  answers={answers}
                  setAnswers={setAnswers}
                  questions={questions}
                  validate={validate}
                  multipleIndex={parseInt(`${n + 1}${multipleIndex}`) || n + 1}
                  conditions={conditions}
                  guidanceManager={guidanceManager}
                />
              )
            })}
          </div>
        ))}
      {showChildren
        ?.filter((c) => c.answerId === value?.answerId && (c.condition ? conditions[c.condition](c.childId) : true))
        .map(({ childId }) => {
          const question = questions.find(({ id }) => id === childId)
          return (
            <QuestionType
              key={`${question.id}-${multipleIndex}`}
              question={question}
              isValidating={isValidating}
              answers={answers}
              setAnswers={setAnswers}
              questions={questions}
              multipleIndex={multipleIndex}
              validate={validate}
              disabled={false}
              conditions={conditions}
              guidanceManager={guidanceManager}
            />
          )
        })}
    </>
  )
}

export default function AuditComponent({
  index,
  auditId,
  criteriaId,
  questions = [],
  grading = [],
  isValidating = false,
  answers,
  setAnswers,
  hideSilver = false,
  perComplete,
  validate,
  optional,
  notGraded,
  module,
  tooltip,
  showProgressBar = false
}) {
  // setDefaultOpenGroup is not used so disabling linting for this line
  // eslint-disable-next-line no-unused-vars
  const [defaultOpenGroup, setDefaultOpenGroup] = useState(answers.gradeId ? grading.find((g) => g.id === answers.gradeId)?.name.toLowerCase() : grading[1]?.name.toLowerCase())

  const orderedQuestions = useMemo(() => questions.sort((a, b) => a.index - b.index), [questions])
  const groupQuestions = useMemo(
    () =>
      orderedQuestions
    .filter(({ parent }) => !parent.length)
    .sort((a, b) => a.index - b.index)
    .reduce((groups, item) => {
      const group = groups[item.group] || []
      group.push(item)
      groups[item.group] = group
      return groups
    }, {}),
    [orderedQuestions]
  )

  const isCriteriaMet = (currentId, { id, children }) => {
    if (id === currentId) return true

    const answer = answers.answers?.find((a) => a.questionId === id)
    const isChildrenMet =
      !children?.length ||
      children
        ?.filter((c) => c.answerId)
        .every((q) => {
          const question = questions.find(({ id }) => q.childId === id)
          return isCriteriaMet(currentId, question)
        })

    return ['Not applicable', 'Criteria met'].includes(answer?.inputLabel) && isChildrenMet
  }

  const conditions = {
    /* true if all visible enabled answers have been met */
    ALL_MET: (questionId) => Object.values(groupQuestions)?.every((g) => g.every((q) => isCriteriaMet(questionId, q)))
  }

  const guidanceManager = useGuidanceManager()

  return (
    <>
      {!notGraded && !optional && (
        <Rating active={answers.gradeId} grading={grading} hideSilver={hideSilver} perComplete={perComplete} module={module} showProgressBar={showProgressBar} />
      )}
      <div className='audit-wrapper'>
        <CriteriaComment className='audit-title' auditId={auditId} criteriaId={criteriaId} />
        <div className='audit-component'>
          <div className='qa-title'>
            <h2>QA Criteria</h2>
            {!!tooltip && <ToolTip title="What does 'good' look like?" text={tooltip} />}
            {gradingFlow(module)}
          </div>
          <div>
            {Object.keys(groupQuestions).map((group) => {
              const index = grading.findIndex((g) => g.name.toLowerCase() === group.toLowerCase())
              const colour = grading[index]?.colour
              return (
                <div key={group}>
                  {group !== 'null' ? (
                    <Accordion key={group} title={`${group[0].toUpperCase()}${group.slice(1)}`} openByDefault={group === defaultOpenGroup} colour={colour}>
                      {groupQuestions[group].map((question) => (
                        <QuestionType
                          key={question.id}
                          question={question}
                          isValidating={isValidating}
                          answers={answers}
                          setAnswers={setAnswers}
                          questions={orderedQuestions}
                          validate={validate}
                          guidanceManager={guidanceManager}
                        />
                      ))}
                    </Accordion>
                  ) : (
                    <>
                      {groupQuestions[group].map((question) => (
                        <QuestionType
                          key={question.id}
                          question={question}
                          isValidating={isValidating}
                          answers={answers}
                          setAnswers={setAnswers}
                          questions={orderedQuestions}
                          validate={validate}
                          conditions={conditions}
                          guidanceManager={guidanceManager}
                        />
                      ))}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}
